import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { hasPermission, getApiHeaders } from '@lognai/plugin-sdk';
import type {
  ChartThemePayload,
  VisualAnswer,
  ThemeAppearance,
  SemanticFilter,
} from '@lognai/plugin-analytics';
import {
  Button,
  Dialog,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DropdownMenu,
  Sheet,
  useTheme,
} from '@lognai/synapse-design-system-v2';
import {
  ArrowLeft,
  Download,
  Loader2,
  RefreshCw,
  RotateCcw,
  Maximize2,
  X,
} from '@lognai/synapse-design-system-v2/icons';
import { HighchartsRenderer, type DrillSelection } from './HighchartsRenderer';
import { ChartInsightWorkspace, type ChartInsightContext } from './ChartInsightWorkspace';
import { VisualDataTable } from './VisualDataTable';
import { VisualSources } from './VisualSources';
import { InsightPanel } from './InsightPanel';
import { askAnalytics, executeAnalysis, type AnalysisResult } from './api';
import { apiFetch, apiPost } from '../api/client';
import { drillAnalysis, semanticFilterForPoint, formatDrillCrumb } from './drill';
import { DATASET_CHANGED } from './dataset-refresh';
import { businessLabel, userFacingAnalyticsError } from '../lib/user-facing';
import { AskLognAIIcon } from '@lognai/plugin-ai-core-ui';
import { themeSurfaceStyle } from './theme-surface';
import { DEFAULT_UI_CHART_THEME } from './chart-theme';
import { formatMetric } from '../lib/format';
import { exportChartImage } from './chart-image-export';
import './theme-surface.css';
import './theme-workspace.css';
import './visual-detail.css';

type Answer = { question?: string; answer: VisualAnswer; version: string };
const versionKey = (result?: AnalysisResult) =>
  JSON.stringify({
    versions: result?.context?.datasetVersions,
    analysis: result?.analysis,
    internal: result?.context?.internalFreshness,
  });
const STARTERS = [
  'What stands out in this visual?',
  'What is driving these results?',
  'What related data should I investigate?',
];

export function VisualDetailModal({
  open,
  onOpenChange,
  result,
  selection,
  theme,
  actions,
  appearance,
  businessQuestion,
  description,
  onApplyFilters,
  insightContext,
  initialPane = 'chart',
}: {
  insightContext?: ChartInsightContext;
  initialPane?: 'chart' | 'insights';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result?: AnalysisResult;
  selection?: DrillSelection;
  theme?: ChartThemePayload;
  actions?: ReactNode;
  appearance?: ThemeAppearance;
  businessQuestion?: string;
  description?: string;
  onApplyFilters?: (filters: SemanticFilter[]) => void;
}) {
  const [active, setActive] = useState(result);
  const [refreshRequested, setRefreshRequested] = useState(false);
  const [trail, setTrail] = useState<AnalysisResult['analysis'][]>([]);
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [visualBusy, setVisualBusy] = useState(false),
    [askBusy, setAskBusy] = useState(false),
    [historyBusy, setHistoryBusy] = useState(false);
  const [error, setError] = useState(''),
    [askError, setAskError] = useState(''),
    [recordsError, setRecordsError] = useState('');
  const [view, setView] = useState<'chart' | 'records'>('chart');
  const [pane, setPane] = useState('chart');
  const [assistOpen, setAssistOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [mobile, setMobile] = useState(
    () => window.matchMedia?.('(max-width: 1023px)').matches ?? false
  );
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 1023px)');
    if (!media) return;
    const update = () => setMobile(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const [drillLabels, setDrillLabels] = useState<string[]>([]);
  const [applied, setApplied] = useState(false);
  const chartHost = useRef<HTMLDivElement>(null);
  const imageHost = useRef<HTMLDivElement>(null);
  const assistButton = useRef<HTMLButtonElement>(null);
  const questionInput = useRef<HTMLTextAreaElement>(null);
  const [chartHeight, setChartHeight] = useState(360);
  const { theme: appAppearance } = useTheme();
  const resolvedAppearance = appearance ?? appAppearance;
  const resolvedTheme = theme ?? DEFAULT_UI_CHART_THEME;
  const [records, setRecords] = useState<Record<string, unknown>[]>([]);
  const [recordsBusy, setRecordsBusy] = useState(false),
    [recordsRetry, setRecordsRetry] = useState(0);
  const [offset, setOffset] = useState(0),
    [search, setSearch] = useState(''),
    [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState(''),
    [direction, setDirection] = useState<'asc' | 'desc'>('asc'),
    [total, setTotal] = useState(0);
  const [exportJob, setExportJob] = useState<string>(),
    [exportBusy, setExportBusy] = useState(false),
    [exportError, setExportError] = useState('');
  const sequence = useRef(0),
    session = useRef(0),
    conversation = useRef<HTMLDivElement>(null);
  const executionId = useRef(active?.context?.executionId);
  executionId.current = active?.context?.executionId;
  const currentVersion = versionKey(active);

  const load = useCallback(
    async (
      analysis: AnalysisResult['analysis'],
      nextTrail = trail,
      nextLabels = drillLabels.slice(0, nextTrail.length)
    ) => {
      const request = ++sequence.current;
      setVisualBusy(true);
      setError('');
      try {
        const next = await executeAnalysis(analysis);
        if (request === sequence.current) {
          setActive(next);
          setTrail(nextTrail);
          setDrillLabels(nextLabels);
          setApplied(false);
          setOffset(0);
        }
      } catch (cause) {
        if (request === sequence.current) setError(userFacingAnalyticsError(cause));
      } finally {
        if (request === sequence.current) setVisualBusy(false);
      }
    },
    [trail, drillLabels]
  );
  useEffect(() => {
    if (!open) return;
    const request = ++sequence.current;
    session.current++;
    setActive(result);
    setAnswers([]);
    setQuestion('');
    setTrail([]);
    setOffset(0);
    setRecords([]);
    setView('chart');
    setSearch('');
    setSearchInput('');
    setSort('');
    setDirection('asc');
    setPane(initialPane);
    setAssistOpen(false);
    setFullscreen(false);
    setDrillLabels([]);
    setApplied(false);
    setError('');
    setAskError('');
    setRecordsError('');
    setAskBusy(false);
    setVisualBusy(false);
    setHistoryBusy(false);
    setRecordsBusy(false);
    setRefreshRequested(false);
    setExportJob(undefined);
    setExportBusy(false);
    setExportError('');
    if (selection && result) {
      setVisualBusy(true);
      void executeAnalysis({
        ...result.analysis,
        filters: [
          ...result.analysis.filters,
          semanticFilterForPoint(selection.member, selection.point),
        ],
      })
        .then((next) => {
          if (request === sequence.current) {
            setActive(next);
            setTrail([result.analysis]);
            setDrillLabels([
              formatDrillCrumb({ member: selection.member, label: selection.point.label }),
            ]);
          }
        })
        .catch((cause) => {
          if (request === sequence.current) setError(userFacingAnalyticsError(cause));
        })
        .finally(() => {
          if (request === sequence.current) setVisualBusy(false);
        });
    }
    return () => {
      sequence.current++;
      session.current++;
    };
  }, [open, result, selection, initialPane]);
  useEffect(() => {
    if (!open || !active) return;
    const refresh = () => setRefreshRequested(true);
    window.addEventListener(DATASET_CHANGED, refresh);
    return () => window.removeEventListener(DATASET_CHANGED, refresh);
  }, [open, active]);
  useEffect(() => {
    if (
      open &&
      active &&
      refreshRequested &&
      !askBusy &&
      !visualBusy &&
      !Object.keys(active.context?.datasetCheckpoints ?? {}).length
    ) {
      setRefreshRequested(false);
      void load(active.analysis);
    }
  }, [open, active, refreshRequested, askBusy, visualBusy, load]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  useEffect(() => {
    if (!open || !assistOpen || !active?.context) return;
    let live = true;
    setHistoryBusy(true);
    void apiFetch<{
      items: Array<{
        question?: string;
        answer: VisualAnswer;
        versions: Record<string, number>;
        analysis: AnalysisResult['analysis'];
        internalFreshness?: string;
      }>;
    }>(`/api/analytics/executions/${active.context.executionId}/answers`)
      .then((response) => {
        if (live)
          setAnswers(
            response.items.map((item) => ({
              question: item.question,
              answer: item.answer,
              version: JSON.stringify({
                versions: Object.fromEntries(
                  Object.keys(active.context!.datasetVersions).map((key) => [
                    key,
                    item.versions[key],
                  ])
                ),
                analysis: item.analysis,
                internal: item.internalFreshness,
              }),
            }))
          );
      })
      .catch(() => {
        if (live)
          setAskError('Previous answers could not be loaded. You can still ask a new question.');
      })
      .finally(() => {
        if (live) setHistoryBusy(false);
      });
    return () => {
      live = false;
    };
  }, [open, assistOpen, active?.context]);
  useEffect(() => {
    if (!open || pane !== 'data' || view !== 'records' || !active?.context) return;
    let live = true;
    setRecordsBusy(true);
    setRecordsError('');
    setRecords([]);
    const params = new URLSearchParams({
      offset: String(offset),
      pageSize: '100',
      search,
      sort,
      direction,
    });
    void apiFetch<{ items: Record<string, unknown>[]; total: number }>(
      `/api/analytics/executions/${active.context.executionId}/records?${params}`
    )
      .then((response) => {
        if (live) {
          setRecords(response.items);
          setTotal(response.total);
        }
      })
      .catch((cause) => {
        if (live) setRecordsError(userFacingAnalyticsError(cause));
      })
      .finally(() => {
        if (live) setRecordsBusy(false);
      });
    return () => {
      live = false;
    };
  }, [open, pane, view, active?.context, offset, search, sort, direction, recordsRetry]);
  useEffect(() => {
    if (!open || !exportJob) return;
    let live = true,
      downloading = false;
    const timer = setInterval(() => {
      if (downloading) return;
      void apiFetch<{ state: string; url?: string; error?: string }>(
        `/api/analytics/exports/${exportJob}`
      )
        .then(async (response) => {
          if (!live || downloading) return;
          if (response.state === 'failed') throw new Error(response.error ?? 'Export failed');
          if (!response.url) return;
          downloading = true;
          const file = await fetch(response.url, { headers: getApiHeaders() });
          if (!file.ok) throw new Error('Export access changed or download failed');
          const blob = await file.blob();
          if (!live) return;
          const url = URL.createObjectURL(blob),
            link = document.createElement('a');
          link.href = url;
          link.download = 'analytics-export.csv';
          link.click();
          URL.revokeObjectURL(url);
          setExportJob(undefined);
        })
        .catch((cause) => {
          if (live) {
            setExportError(userFacingAnalyticsError(cause));
            setExportJob(undefined);
          }
        });
    }, 1000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [open, exportJob]);
  useEffect(() => {
    conversation.current?.scrollTo?.({
      top: conversation.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [answers.length, askBusy]);

  const drill = (picked: DrillSelection) => {
    if (!active || visualBusy || askBusy) return;
    const next = drillAnalysis(active.analysis, picked.member, picked.point) ?? {
      ...active.analysis,
      filters: [
        ...active.analysis.filters,
        picked.filter ?? semanticFilterForPoint(picked.member, picked.point),
      ],
    };
    void load(
      next,
      [...trail, active.analysis],
      [...drillLabels, formatDrillCrumb({ member: picked.member, label: picked.point.label })]
    );
  };
  const ask = async () => {
    if (!active || !question.trim() || askBusy || historyBusy || visualBusy) return;
    const submitted = question.trim(),
      version = currentVersion,
      requestSession = session.current,
      id = active.context?.executionId;
    setAskBusy(true);
    setAskError('');
    try {
      const answer = await askAnalytics({
        message: submitted,
        activeAnalysis: active.analysis,
        executionId: id,
        history: answers
          .slice(-3)
          .flatMap((item) => [
            ...(item.question ? [{ role: 'user' as const, content: item.question }] : []),
            { role: 'assistant' as const, content: item.answer.insight.summary },
          ]),
      });
      if (session.current === requestSession && executionId.current === id) {
        setAnswers((previous) => [...previous, { question: submitted, answer, version }]);
        setQuestion('');
      }
    } catch (cause) {
      if (session.current === requestSession) setAskError(userFacingAnalyticsError(cause));
    } finally {
      if (session.current === requestSession) setAskBusy(false);
    }
  };
  const exportData = async (kind: 'chart' | 'records') => {
    if (!active?.context || exportBusy || exportJob) return;
    const requestSession = session.current;
    setExportBusy(true);
    setExportError('');
    try {
      const response = await apiPost<{ id: string }>(
        `/api/analytics/executions/${active.context.executionId}/exports`,
        { kind }
      );
      if (session.current === requestSession) setExportJob(response.id);
    } catch (cause) {
      if (session.current === requestSession) setExportError(userFacingAnalyticsError(cause));
    } finally {
      if (session.current === requestSession) setExportBusy(false);
    }
  };
  const fields = active?.context?.sources[0]?.fields ?? active?.context?.recordColumns ?? [];
  const hasRecords = !!fields.length && !!active?.context && !active.analysis.baselineComparison;
  const label = (member: string) =>
    fields.find((field) => member.split('.')[1] === field.id)?.name ?? businessLabel(member);
  const allBusy = visualBusy || askBusy;
  const changedFilters =
    active?.analysis.filters.filter(
      (filter) =>
        !result?.analysis.filters.some(
          (original) => JSON.stringify(original) === JSON.stringify(filter)
        )
    ) ?? [];
  const nextMember =
    active?.analysis.drillPath[
      (active.analysis.drillPath.indexOf(active.visual.categoryMember ?? '') ?? -1) + 1
    ];
  const primaryEvidence = active?.evidence[0];
  const summary =
    primaryEvidence && Number.isFinite(Number(primaryEvidence.value))
      ? formatMetric(Number(primaryEvidence.value), primaryEvidence.unit ?? 'number')
      : undefined;
  useLayoutEffect(() => {
    const element = chartHost.current;
    if (!open || pane !== 'chart' || !element) return;
    const measure = () => setChartHeight(Math.max(180, element.clientHeight));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [open, pane, assistOpen, fullscreen, !!active]);
  useEffect(() => {
    if (assistOpen) questionInput.current?.focus();
  }, [assistOpen]);
  const closeAssist = () => {
    setAssistOpen(false);
    assistButton.current?.focus();
  };
  const assistContent = (
    <>
      <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
        <h3 className="flex items-center gap-2 font-semibold text-primary">
          <AskLognAIIcon size={17} />
          Assist
        </h3>
        <Button size="xs" variant="ghost" aria-label="Close Assist" onClick={closeAssist}>
          <X size={16} />
        </Button>
      </div>
      <div
        ref={conversation}
        aria-label="Conversation"
        tabIndex={0}
        className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain bg-surface px-4 pb-5 pt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        {historyBusy && (
          <p role="status" className="text-xs text-muted-foreground">
            Loading previous answers…
          </p>
        )}
        {!answers.length && !historyBusy && (
          <div className="space-y-4 pb-3">
            <section className="border-b border-primary/25 pb-4">
              <h4 className="text-sm font-semibold">Go beyond the chart</h4>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Understand a change, investigate a driver, or connect this visual with other
                business context.
              </p>
            </section>
            <p className="text-xs font-medium text-muted-foreground">Try asking</p>
            {STARTERS.map((starter) => (
              <button
                key={starter}
                className="block w-full rounded-lg border border-border-strong bg-surface px-3 py-3 text-left text-sm shadow-sm transition-colors hover:border-primary/25 hover:bg-primary-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setQuestion(starter)}
              >
                {starter}
              </button>
            ))}
          </div>
        )}
        {answers.map((item, index) => (
          <article key={index} className="space-y-3">
            {item.question && (
              <p className="ml-5 rounded-xl border border-primary/25 bg-primary-subtle px-4 py-3 text-sm leading-6">
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">You</span>
                {item.question}
              </p>
            )}
            {item.version !== currentVersion && (
              <div className="rounded-lg border border-warning-border bg-warning-subtle p-3 text-xs text-warning-foreground">
                <p>This answer uses earlier data. Ask again to reanalyze.</p>
                {item.question && (
                  <Button size="xs" variant="ghost" onClick={() => setQuestion(item.question!)}>
                    Reanalyse this question
                  </Button>
                )}
              </div>
            )}
            <div className="overflow-hidden rounded-xl border border-border-strong bg-surface shadow-sm">
              <InsightPanel
                insight={item.answer.insight}
                evidence={item.answer.evidence}
                trace={item.answer.trace}
                onSuggestedQuestion={setQuestion}
                disabled={askBusy}
              />
            </div>
          </article>
        ))}
        {askBusy && (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={15} className="animate-spin" /> Analysing this visual…
          </p>
        )}
      </div>
      <form
        className="shrink-0 space-y-3 border-t-2 border-primary/25 bg-surface p-4"
        onSubmit={(event) => {
          event.preventDefault();
          void ask();
        }}
      >
        {askError && (
          <p role="alert" className="text-xs text-destructive">
            {askError}
          </p>
        )}
        <label htmlFor="visual-question" className="sr-only">
          Question for Ask LognAI
        </label>
        <textarea
          ref={questionInput}
          id="visual-question"
          aria-label="Question for Ask LognAI"
          rows={3}
          className="max-h-32 min-h-[84px] w-full resize-none rounded-xl border border-border-strong bg-background px-3 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          value={question}
          maxLength={1000}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Ask about this visual…"
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void ask();
            }
          }}
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">⌘ / Ctrl + Enter to send</span>
          <Button
            type="submit"
            size="sm"
            disabled={!active || allBusy || historyBusy || !question.trim()}
          >
            {askBusy ? 'Analysing…' : 'Ask'}
          </Button>
        </div>
      </form>
    </>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        style={themeSurfaceStyle(resolvedTheme, resolvedAppearance)}
        data-theme={resolvedAppearance}
        className={`visual-detail-modal analytics-themed-surface overflow-hidden p-0 ${fullscreen ? 'h-[100dvh] max-h-[100dvh] w-screen max-w-none rounded-none' : 'h-[100dvh] max-h-[100dvh] w-screen max-w-none rounded-none sm:h-[94dvh] sm:max-h-[94dvh] sm:w-[96vw] sm:max-w-[1600px] sm:rounded-xl'}`}
        onEscapeKeyDown={(event) => {
          if (assistOpen) {
            event.preventDefault();
            closeAssist();
          }
        }}
      >
        <Dialog.Header className="shrink-0 border-b border-border bg-surface px-4 py-4 pr-12 sm:px-6 sm:pr-14">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-xl">
                {active?.analysis.title ?? 'Chart details'}
              </Dialog.Title>
              <Dialog.Description className="mt-1">
                {businessQuestion || description || active?.visual.title || 'Chart details'}
              </Dialog.Description>
            </div>
            {summary && <strong className="text-2xl tabular-nums">{summary}</strong>}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                ref={assistButton}
                size="sm"
                variant="outline"
                className="theme-assist-button relative overflow-hidden"
                aria-expanded={assistOpen}
                aria-controls="visual-assist-panel"
                leftIcon={<AskLognAIIcon size={16} />}
                onClick={() => (assistOpen ? closeAssist() : setAssistOpen(true))}
              >
                Assist
              </Button>
              {hasPermission('analytics:dashboard:export') && (
                <DropdownMenu>
                  <DropdownMenu.Trigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!active || visualBusy || exportBusy || !!exportJob}
                      leftIcon={<Download size={15} />}
                    >
                      {exportBusy || exportJob ? 'Preparing export…' : 'Export'}
                    </Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end">
                    <DropdownMenu.Item
                      disabled={!active}
                      onSelect={() => {
                        setPane('chart');
                        requestAnimationFrame(() => {
                          if (imageHost.current) {
                            try {
                              exportChartImage(imageHost.current, active!.analysis.title);
                            } catch (cause) {
                              setExportError(
                                userFacingAnalyticsError(
                                  cause,
                                  'The chart image could not be exported.'
                                )
                              );
                            }
                          }
                        });
                      }}
                    >
                      Chart image (SVG)
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      disabled={!active?.context}
                      onSelect={() => void exportData('chart')}
                    >
                      Chart values CSV
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      disabled={!hasRecords}
                      onSelect={() => void exportData('records')}
                    >
                      Underlying records CSV
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator />
                    <div className="max-w-64 px-2 py-2 text-xs text-muted-foreground">
                      Exports use the current drill and filters. CSV exports include all matching
                      rows; table search does not change them.
                    </div>
                  </DropdownMenu.Content>
                </DropdownMenu>
              )}
              <Button
                size="sm"
                variant="ghost"
                aria-label={fullscreen ? 'Restore modal size' : 'Fullscreen chart'}
                onClick={() => setFullscreen(!fullscreen)}
              >
                <Maximize2 size={17} />
              </Button>
            </div>
          </div>
        </Dialog.Header>
        <Tabs value={pane} onValueChange={setPane} className="flex min-h-0 flex-1 flex-col">
          <TabsList aria-label="Chart detail views" className="shrink-0 justify-start px-4 sm:px-6">
            <TabsTrigger value="chart">Chart</TabsTrigger>
            <TabsTrigger value="data">Data</TabsTrigger>
            <TabsTrigger value="sources">Sources</TabsTrigger>
            {insightContext && <TabsTrigger value="insights">Insights</TabsTrigger>}
            <Button
              className={`ml-auto ${refreshRequested ? 'text-primary' : ''}`}
              size="xs"
              variant="ghost"
              disabled={!active || allBusy}
              title={
                refreshRequested
                  ? 'New data is available. Refresh to use the latest checkpoint.'
                  : 'Refresh data'
              }
              aria-label="Refresh data"
              onClick={() => active && void load(active.analysis)}
            >
              <RefreshCw size={14} className={visualBusy ? 'animate-spin' : ''} />
            </Button>
          </TabsList>
          {active && (
            <div className="shrink-0 space-y-2 border-b border-border px-4 py-2 sm:px-6">
              <div className="flex flex-wrap items-center gap-2">
                <nav
                  aria-label="Chart drill path"
                  className="flex min-w-0 flex-wrap items-center gap-1 text-sm"
                >
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={!trail.length || allBusy}
                    onClick={() => result && void load(result.analysis, [], [])}
                  >
                    All{' '}
                    {result?.visual.categoryMember ? label(result.visual.categoryMember) : 'data'}
                  </Button>
                  {drillLabels.map((crumb, index) => (
                    <span key={`${index}-${crumb}`} className="inline-flex items-center gap-1">
                      <span aria-hidden="true">›</span>
                      <Button
                        size="xs"
                        variant="ghost"
                        aria-current={index === drillLabels.length - 1 ? 'page' : undefined}
                        disabled={allBusy || index === drillLabels.length - 1}
                        onClick={() =>
                          void load(
                            trail[index + 1],
                            trail.slice(0, index + 1),
                            drillLabels.slice(0, index + 1)
                          )
                        }
                      >
                        {crumb}
                      </Button>
                    </span>
                  ))}
                </nav>
                {trail.length > 0 && (
                  <>
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={allBusy}
                      onClick={() => void load(trail[trail.length - 1], trail.slice(0, -1))}
                    >
                      <ArrowLeft size={13} />
                      Back
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={allBusy}
                      onClick={() => result && void load(result.analysis, [], [])}
                    >
                      <RotateCcw size={13} />
                      Reset view
                    </Button>
                  </>
                )}
                {onApplyFilters && changedFilters.length > 0 && (
                  <Button
                    size="xs"
                    variant="outline"
                    className="ml-auto"
                    disabled={allBusy || applied}
                    onClick={() => {
                      onApplyFilters(changedFilters);
                      setApplied(true);
                    }}
                  >
                    {applied ? 'Applied to dashboard' : 'Apply to dashboard'}
                  </Button>
                )}
              </div>
              {!!active.analysis.filters.length && (
                <div aria-label="Active filters" className="flex flex-wrap gap-2">
                  {active.analysis.filters.map((filter, index) => (
                    <span
                      key={`${filter.member}-${index}`}
                      className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground"
                    >
                      {label(filter.member)} ·{' '}
                      {filter.values?.join(', ') || businessLabel(filter.operator)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {(error || exportError) && (
            <p role="alert" className="shrink-0 px-4 py-2 text-sm text-destructive">
              {error || exportError}
            </p>
          )}
          <div
            className={`visual-detail-body relative flex min-h-0 flex-1 overflow-hidden bg-background ${assistOpen ? 'visual-detail-body--assist' : ''}`}
          >
            <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4 sm:p-5">
              <TabsContent
                value="chart"
                className="m-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
              >
                {active ? (
                  <div
                    ref={imageHost}
                    aria-label="Chart"
                    className="analytics-widget-card flex min-h-0 flex-1 flex-col overflow-hidden border border-border bg-surface"
                  >
                    <header className="shrink-0 px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="font-semibold">{active.analysis.title}</h2>
                        {summary && <strong className="tabular-nums">{summary}</strong>}
                      </div>
                      {businessQuestion && (
                        <p className="mt-1 text-sm text-muted-foreground">{businessQuestion}</p>
                      )}
                    </header>
                    <div
                      ref={chartHost}
                      aria-busy={visualBusy}
                      className="visual-detail-chart relative min-h-0 flex-1 overflow-auto"
                    >
                      <HighchartsRenderer
                        visual={active.visual}
                        height={chartHeight}
                        appearance={resolvedAppearance}
                        theme={resolvedTheme}
                        hideExportMenu
                        fitHeight
                        onSelect={drill}
                      />
                      {visualBusy && (
                        <div
                          role="status"
                          className="absolute inset-0 grid place-items-center bg-surface/70"
                        >
                          <span className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm">
                            <Loader2 size={16} className="animate-spin" />
                            Updating chart…
                          </span>
                        </div>
                      )}
                    </div>
                    <footer className="shrink-0 border-t border-border px-4 py-2 text-xs text-muted-foreground">
                      {description && <p className="mb-1">{description}</p>}
                      {nextMember
                        ? `Select a value to drill to ${label(nextMember)}.`
                        : active.visual.categoryMember
                          ? 'Select a value to filter this chart.'
                          : 'Explore chart values in the Data tab.'}
                    </footer>
                  </div>
                ) : (
                  <p role="status">Loading chart…</p>
                )}
              </TabsContent>
              <TabsContent
                value="data"
                className="m-0 min-h-0 flex-1 overflow-auto data-[state=inactive]:hidden"
              >
                {active && (
                  <section
                    aria-label="Supporting data"
                    className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-surface"
                  >
                    <Tabs
                      value={view}
                      className="shrink-0"
                      onValueChange={(value) => setView(value as 'chart' | 'records')}
                    >
                      <TabsList aria-label="Table data" className="justify-start px-4">
                        <TabsTrigger value="chart">Chart values</TabsTrigger>
                        <TabsTrigger
                          value="records"
                          disabled={!hasRecords}
                          title={
                            !hasRecords
                              ? 'Underlying records are unavailable for this chart'
                              : undefined
                          }
                        >
                          Underlying records
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    {view === 'chart' ? (
                      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4 [&_thead]:bg-muted [&_tbody_tr:nth-child(even)]:bg-muted/40">
                        {(active.rows ?? []).length ? (
                          <VisualDataTable
                            analysis={active.analysis}
                            visual={active.visual}
                            rows={active.rows}
                            fields={fields}
                            fillHeight
                          />
                        ) : (
                          <p className="py-8 text-center text-sm text-muted-foreground">
                            No chart values match the current filters.
                          </p>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="flex shrink-0 flex-wrap gap-2 border-b border-border bg-background px-4 py-3">
                          <Input
                            className="min-w-[160px] flex-1"
                            aria-label="Search records"
                            placeholder="Search these records…"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                          />
                          <select
                            aria-label="Sort records"
                            className="min-w-0 rounded-md border border-border bg-surface px-3 text-sm"
                            value={sort}
                            onChange={(event) => {
                              setSort(event.target.value);
                              setOffset(0);
                            }}
                          >
                            <option value="">Original order</option>
                            {fields.map((field) => (
                              <option key={field.id} value={field.id}>
                                {field.name}
                              </option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!sort}
                            aria-label="Toggle record sort direction"
                            onClick={() => {
                              setDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
                              setOffset(0);
                            }}
                          >
                            {direction === 'asc' ? 'Ascending' : 'Descending'}
                          </Button>
                        </div>
                        {recordsError ? (
                          <div role="alert" className="px-4 py-5 text-sm text-destructive">
                            {recordsError}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setRecordsRetry((value) => value + 1)}
                            >
                              Retry records
                            </Button>
                          </div>
                        ) : recordsBusy ? (
                          <p
                            role="status"
                            className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground"
                          >
                            <Loader2 size={16} className="animate-spin" /> Loading records…
                          </p>
                        ) : !records.length ? (
                          <p className="py-10 text-center text-sm text-muted-foreground">
                            No records match your filters and search.
                          </p>
                        ) : (
                          <div className="min-h-0 flex-1 overflow-auto">
                            <table className="w-full text-left text-sm">
                              <caption className="sr-only">
                                Underlying records for {active.analysis.title}
                              </caption>
                              <thead className="sticky top-0 z-10 bg-muted">
                                <tr>
                                  {fields.map((field) => (
                                    <th
                                      scope="col"
                                      className={`whitespace-nowrap border-y border-border px-4 py-3 text-xs font-medium ${field.type === 'decimal' ? 'text-right' : ''}`}
                                      key={field.id}
                                    >
                                      {field.name}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {records.map((record, index) => (
                                  <tr
                                    key={String(record.row_number ?? index)}
                                    className="even:bg-muted/40 hover:bg-muted"
                                  >
                                    {fields.map((field) => (
                                      <td
                                        className={`max-w-[320px] break-words border-b border-border/50 px-4 py-2 ${field.type === 'decimal' ? 'text-right tabular-nums' : ''}`}
                                        key={field.id}
                                      >
                                        {record[field.id] == null ? (
                                          <span className="text-muted-foreground">—</span>
                                        ) : typeof record[field.id] === 'object' ? (
                                          JSON.stringify(record[field.id])
                                        ) : (
                                          String(record[field.id])
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <p className="shrink-0 px-4 py-2 text-xs text-muted-foreground">
                          Exports include all records for the visual’s filters. Table search and
                          sorting apply only to this preview.
                        </p>
                        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-surface px-4 py-3 text-xs text-muted-foreground">
                          <span aria-live="polite">
                            {recordsBusy
                              ? 'Loading…'
                              : `${total ? offset + 1 : 0}–${Math.min(offset + records.length, total)} of ${total.toLocaleString()} filtered records`}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              size="xs"
                              variant="ghost"
                              disabled={!offset || recordsBusy}
                              onClick={() => setOffset(Math.max(0, offset - 100))}
                            >
                              Previous
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              disabled={
                                recordsBusy || !!recordsError || offset + records.length >= total
                              }
                              onClick={() => setOffset(offset + 100)}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </section>
                )}
              </TabsContent>
              <TabsContent
                value="sources"
                className="m-0 min-h-0 flex-1 overflow-auto data-[state=inactive]:hidden"
              >
                {active && <VisualSources result={active} expanded />}
              </TabsContent>
              {insightContext && (
                <TabsContent
                  value="insights"
                  className="m-0 min-h-0 flex-1 overflow-auto data-[state=inactive]:hidden"
                >
                  {open && pane === 'insights' && (
                    <ChartInsightWorkspace
                      context={insightContext}
                      result={active}
                      onAsk={(value) => {
                        setQuestion(value);
                        setAssistOpen(true);
                      }}
                    />
                  )}
                </TabsContent>
              )}
            </div>
            {assistOpen && !mobile && (
              <aside
                id="visual-assist-panel"
                aria-label="Assist panel"
                className="visual-detail-assist flex min-h-0 min-w-0 flex-col border-l border-border bg-surface"
              >
                {assistContent}
              </aside>
            )}
            {mobile && (
              <Sheet
                open={assistOpen}
                onOpenChange={(next) => (next ? setAssistOpen(true) : closeAssist())}
              >
                <Sheet.Content
                  side="right"
                  style={themeSurfaceStyle(resolvedTheme, resolvedAppearance)}
                  className="flex h-full w-full max-w-md flex-col gap-0 p-0 [&>button]:hidden"
                >
                  <Sheet.Header className="sr-only">
                    <Sheet.Title>Chart Assist</Sheet.Title>
                    <Sheet.Description>Ask about the current chart and filters.</Sheet.Description>
                  </Sheet.Header>
                  <div id="visual-assist-panel" className="flex min-h-0 flex-1 flex-col">
                    {assistContent}
                  </div>
                </Sheet.Content>
              </Sheet>
            )}
          </div>
        </Tabs>
        {actions && (
          <Dialog.Footer className="shrink-0 border-t border-border bg-surface px-5 py-3">
            {actions}
          </Dialog.Footer>
        )}
      </Dialog.Content>
    </Dialog>
  );
}
